import { useState } from 'react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { getActiveDates } from '../store/useStore';
import { useValidators } from '../hooks/useValidators';
import { ArrowLeft, Users, X, Plus, CheckCircle2, ClipboardList, Flame, Check, Music, UtensilsCrossed, Search, Edit2, FileSpreadsheet } from 'lucide-react';
import { CategoryDrawer, groupPointsByCategory } from './CategoryDrawer';
import { OperatingHoursExportModal } from './OperatingHoursExportModal';

const parseDateUTCStr = (dStr: string) => {
  if (dStr.endsWith('Z')) return dStr;
  if (dStr.length === 16) return dStr + ':00Z';
  return dStr + 'Z';
};

export function MatrixView() {
  const { points, allPoints, shifts, addShift, updateShift, deleteShift, team, currentUser, categories, updatePoint, viewAsManagerCategoryId } = useVisibleStore();
  const validators = useValidators();
  const visiblePoints = points;
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);

  const effectiveCategoryId = (currentUser.role === 'manager' && currentUser.categoryId) || viewAsManagerCategoryId;
  const myCategory = effectiveCategoryId ? categories.find(c => c.id === effectiveCategoryId) : undefined;
  const isArtistManager = myCategory?.isArtistCategory === true;
  const isAdmin = currentUser.role === 'admin' && !viewAsManagerCategoryId;
  const [isTeamPanelOpen, setIsTeamPanelOpen] = useState(false);
  const [showHoursExport, setShowHoursExport] = useState(false);

  const selectedPoint = (allPoints || points).find(p => p.id === selectedPointId);
  const selectedPointCat = selectedPoint ? categories.find(c => c.id === selectedPoint.categoryId) : null;
  const isArtistPoint = selectedPointCat?.isArtistCategory === true;

  const visibleTeamForAssignment = (() => {
    if (selectedPoint?.isStage) {
      return team.filter(s => s.isArtist && !s.parentArtistId).sort((a, b) => a.name.localeCompare(b.name));
    }
    if (isArtistPoint && selectedPoint) {
      const eligible = team.filter(s => (s.isArtist && !s.parentArtistId) || s.categoryId === selectedPoint.categoryId);
      return eligible.sort((a, b) => {
        const aArtist = a.isArtist ? 1 : 0;
        const bArtist = b.isArtist ? 1 : 0;
        if (aArtist !== bArtist) return bArtist - aArtist;
        return a.name.localeCompare(b.name);
      });
    }
    return team.filter(s => !s.parentArtistId);
  })();

  const pointDates = selectedPoint ? getActiveDates([selectedPoint]) : [];

  // Helper to check if a specific hour slot is open for a point
  const isSlotOpen = (pointId: string, checkHour: number, dateStr: string) => {
    const point = (allPoints || points).find(p => p.id === pointId);
    if (!point || !dateStr) return false;

    // A cell is an hour from `checkHour:00` to `checkHour:59` 
    // Create Date objects for the cell on the selected date
    const cellStart = new Date(`${dateStr}T${String(checkHour).padStart(2, '0')}:00:00Z`);
    const cellEnd = new Date(`${dateStr}T${String(checkHour).padStart(2, '0')}:59:59Z`);

    return point.openingHours.some(slot => {
      const slotStart = new Date(parseDateUTCStr(slot.start));
      const slotEnd = new Date(parseDateUTCStr(slot.end));
      // Overlap logic: cellStart < slotEnd && cellEnd > slotStart
      return cellStart < slotEnd && cellEnd > slotStart;
    });
  };

  const getSlotShifts = (pointId: string, checkHour: number, dateStr: string) => {
    if (!dateStr) return [];
    const cellStart = new Date(`${dateStr}T${String(checkHour).padStart(2, '0')}:00:00Z`);
    const cellEnd = new Date(`${dateStr}T${String(checkHour).padStart(2, '0')}:59:59Z`);
    
    return shifts.filter(s => {
      if (s.pointId !== pointId) return false;
      const sStart = new Date(parseDateUTCStr(s.start));
      const sEnd = new Date(parseDateUTCStr(s.end));
      return cellStart < sEnd && cellEnd > sStart;
    });
  };

  const getSlotRequiredTeam = (pointId: string, dateStr: string, hour: number) => {
    const point = (allPoints || points).find(p => p.id === pointId);
    if (!point) return 1;

    const cellStart = new Date(`${dateStr}T${String(hour).padStart(2, '0')}:00:00Z`);
    const cellEnd = new Date(`${dateStr}T${String(hour).padStart(2, '0')}:59:59Z`);

    const overlappingSlot = point.openingHours.find(slot => {
      const slotStart = new Date(parseDateUTCStr(slot.start));
      const slotEnd = new Date(parseDateUTCStr(slot.end));
      return cellStart < slotEnd && cellEnd > slotStart;
    });

    return overlappingSlot?.requiredTeam ?? point.requiredTeam ?? 1;
  };

  const getSlotColor = (teamCount: number, requiredTeam: number) => {
    if (teamCount >= requiredTeam) return 'bg-emerald-500/20 border-emerald-500/50';
    if (teamCount > 0) return 'bg-yellow-500/20 border-yellow-500/50';
    return 'bg-red-500/20 border-red-500/50';
  };

  // Modal for Assignment
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState<{ pointId: string, hour: number, dateStr: string } | null>(null);
  
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assignTeamIds, setAssignTeamIds] = useState<string[]>([]);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [assignStartTime, setAssignStartTime] = useState<string>('00:00');
  const [assignEndTime, setAssignEndTime] = useState<string>('03:00');
  const [validationErrors, setValidationErrors] = useState<{type: string, message: string}[]>([]);
  const [checkinModal, setCheckinModal] = useState<{ slotStart: string; slotEnd: string } | null>(null);
  const [editingSlot, setEditingSlot] = useState<{ pointId: string; idx: number; start: string; end: string; requiredTeam: number; originalStart: string; originalEnd: string } | null>(null);

  const handleOpenAssign = (pointId: string, hour: number, dateStr: string, startOverride?: string, endOverride?: string) => {
    setModalData({ pointId, hour, dateStr });
    setIsModalOpen(true);
    setValidationErrors([]);
    setAssignTeamId('');
    setAssignTeamIds([]);
    setTeamSearchQuery('');
    setAssignStartTime(startOverride ?? `${String(hour).padStart(2, '0')}:00`);
    setAssignEndTime(endOverride ?? `${String((hour + 3) % 24).padStart(2, '0')}:00`);
  };

  const isGeneralPoint = categories.find(c => c.id === selectedPoint?.categoryId)?.isGeneral === true;
  const checkinModalShifts = checkinModal && selectedPoint
    ? shifts.filter(sh =>
        sh.pointId === selectedPoint.id &&
        sh.start < checkinModal.slotEnd &&
        sh.end > checkinModal.slotStart
      )
    : [];

  const fmtTime = (iso: string) => iso.split('T')[1]?.substring(0, 5) ?? iso;
  const fmtDate = (iso: string) => new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(iso.split('T')[0] + 'T00:00:00Z'));


  const handleAssign = () => {
    if (!modalData) return;
    if (isGeneralPoint && assignTeamIds.length === 0) return;
    if (!isGeneralPoint && !assignTeamId) return;

    let startStr = `${modalData.dateStr}T${assignStartTime}`;
    
    // Calculate end datetime, adjusting if the time goes to the next day
    let endDtStr = `${modalData.dateStr}T${assignEndTime}`;
    if (assignEndTime <= assignStartTime && assignEndTime !== '00:00') {
       // if end is same or less than start, it means it crossed midnight.
       const dt = new Date(`${modalData.dateStr}T00:00:00Z`);
       dt.setUTCDate(dt.getUTCDate() + 1);
       endDtStr = `${dt.toISOString().split('T')[0]}T${assignEndTime}`;
    }
    // Also if end is 00:00 and start isn't, usually means exact midnight next day
    if (assignEndTime === '00:00' && assignStartTime !== '00:00') {
       const dt = new Date(`${modalData.dateStr}T00:00:00Z`);
       dt.setUTCDate(dt.getUTCDate() + 1);
       endDtStr = `${dt.toISOString().split('T')[0]}T00:00`;
    }

    const endStr = endDtStr;

    let errorsAndWarnings: {type: string, message: string}[] = [];

    if (isGeneralPoint) {
      assignTeamIds.forEach(id => {
        const teamName = team.find(s => s.id === id)?.name || 'Unknown';
        const vals = validators.validateAssignment(id, modalData.pointId, startStr, endStr, undefined, true)
                      .filter(v => v.type === 'error' || v.type === 'warning')
                      .map(v => ({ ...v, message: `${teamName}: ${v.message}` }));
        errorsAndWarnings.push(...vals);
      });
    } else {
      errorsAndWarnings = validators.validateAssignment(assignTeamId, modalData.pointId, startStr, endStr)
                                    .filter(v => v.type === 'error' || v.type === 'warning');
    }
    
    if (errorsAndWarnings.length > 0) {
      // Check if we already showed THESE exact validation issues
      const alreadyShowed = validationErrors.length === errorsAndWarnings.length && 
                            validationErrors.every((v, i) => v.message === errorsAndWarnings[i].message);
                            
      setValidationErrors(errorsAndWarnings);
      
      if (errorsAndWarnings.some(e => e.type === 'error')) {
        return; // BLOCK on errors
      }
      
      if (!alreadyShowed) {
        return; // BLOCK the first time to show warnings, user must click again to override
      }
    }

    if (isGeneralPoint) {
      assignTeamIds.forEach(id => {
        addShift({
          pointId: modalData.pointId,
          teamId: id,
          start: startStr,
          end: endStr,
          description: ''
        });
      });
    } else {
      addShift({
        pointId: modalData.pointId,
        teamId: assignTeamId,
        start: startStr,
        end: endStr,
        description: ''
      });
    }

    setIsModalOpen(false);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const [closedDrawers, setClosedDrawers] = useState<Record<string, boolean>>({});
  const [slotFormPointId, setSlotFormPointId] = useState<string | null>(null);
  const [newSlotDate, setNewSlotDate] = useState('');
  const [newSlotStart, setNewSlotStart] = useState('08:00');
  const [newSlotEnd, setNewSlotEnd] = useState('20:00');
  const [newSlotTeam, setNewSlotTeam] = useState(1);
  const [newSlotIsHard, setNewSlotIsHard] = useState(false);

  const openAddSlotForm = (pointId: string) => {
    const dates = getActiveDates(allPoints);
    const dateToUse = dates.length > 0 ? dates.sort()[0] : new Date().toISOString().split('T')[0];
    setNewSlotDate(dateToUse);
    setNewSlotStart('08:00');
    setNewSlotEnd('20:00');
    const pt = allPoints.find(p => p.id === pointId);
    setNewSlotTeam(pt?.requiredTeam || 1);
    setNewSlotIsHard(false);
    setSlotFormPointId(pointId);
  };

  const handleSaveSlot = () => {
    if (!slotFormPointId) return;
    const point = allPoints.find(p => p.id === slotFormPointId);
    if (!point) return;
    const newSlot = {
      start: `${newSlotDate}T${newSlotStart}:00`,
      end: `${newSlotDate}T${newSlotEnd}:00`,
      requiredTeam: newSlotTeam,
      isHard: newSlotIsHard,
    };
    updatePoint(slotFormPointId, { openingHours: [...point.openingHours, newSlot] });
    setSlotFormPointId(null);
  };

  const formatSlotRange = (start: string, end: string) => {
    const dStart = new Date(parseDateUTCStr(start));
    const dEnd = new Date(parseDateUTCStr(end));
    
    const optionsDate: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' };
    const optionsTime: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' };
    
    const startDay = dStart.toLocaleDateString('fr-FR', optionsDate);
    const startTime = dStart.toLocaleTimeString('fr-FR', optionsTime).replace(':', 'h');
    const endDay = dEnd.toLocaleDateString('fr-FR', optionsDate);
    const endTime = dEnd.toLocaleTimeString('fr-FR', optionsTime).replace(':', 'h');
    
    // Capitalize first letter of day
    const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
    
    if (startDay === endDay) {
      return `${cap(startDay)} de ${startTime} à ${endTime}`;
    } else {
      return `Du ${cap(startDay)} à ${startTime} au ${cap(endDay)} à ${endTime}`;
    }
  };

  if (!selectedPointId || !selectedPoint) {
    return (
      <>
      <div className="p-4 md:p-8 h-full overflow-y-auto overflow-x-hidden relative">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end mb-8 gap-4 relative z-10">
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent mb-2">Planning Matrix</h2>
            <p className="text-slate-400">Select an operational point to view and manage its schedule</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Rechercher un point..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 bg-[#1A1D23] border border-[#2D3139] rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowHoursExport(true)}
              className="flex items-center gap-1.5 px-3 py-2.5 bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 text-indigo-400 hover:text-indigo-300 text-xs font-bold rounded-xl transition-all flex-shrink-0"
              title="Exporter les horaires d'ouverture"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden sm:inline">Export Horaires</span>
            </button>
          </div>
        </div>

        {visiblePoints.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 space-y-4 relative z-10">
            <Users className="w-16 h-16 opacity-20" />
            <p>No operational points registered in this access view. Add some in configuration.</p>
          </div>
        ) : (
          <div className="relative z-10 space-y-0">
            {groupPointsByCategory(
              visiblePoints.filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase())),
              categories,
              { effectiveCategoryId: effectiveCategoryId ?? undefined, isArtistManager }
            ).map(({ category, points }) => {
              const isOpen = searchQuery ? true : !closedDrawers[category.id];
              return (
                <CategoryDrawer
                  key={category.id}
                  category={category}
                  pointCount={points.length}
                  isOpen={isOpen}
                  onToggle={() => setClosedDrawers(prev => ({ ...prev, [category.id]: !prev[category.id] }))}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {points.map(point => {
              const slots = [...point.openingHours].sort((a, b) => a.start.localeCompare(b.start));
              const isExpanded = expandedCards[point.id] || false;
              const visibleSlots = isExpanded ? slots : slots.slice(0, 2);

              const isStage = point.isStage === true;
              const isMeal = point.isMealPoint === true;
              const cardBorder = isStage ? 'border-amber-500/40' : 'border-[#2D3139]';
              const cardHover = isStage ? 'hover:border-amber-500/70 hover:shadow-amber-500/5' : 'hover:border-indigo-500/50 hover:shadow-xl hover:shadow-indigo-500/10';

              return (
                <div
                  key={point.id}
                  className={`bg-[#14161B]/80 backdrop-blur-sm border rounded-2xl p-6 transition-all duration-300 ${cardBorder} ${cardHover} group flex flex-col`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className={`text-xl font-bold transition-colors leading-tight ${isStage ? 'text-amber-400 group-hover:text-amber-300' : 'text-white group-hover:text-indigo-400'}`}>{point.name}</h3>
                    <div className="bg-indigo-500/10 text-indigo-400 px-2.5 py-1 rounded-lg border border-indigo-500/20 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <Users className="w-3 h-3" /> {point.requiredTeam} team
                    </div>
                  </div>

                  {(isStage || isMeal) && (
                    <div className="flex items-center gap-2 mb-3">
                      {isStage && (
                        <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
                          <Music className="w-2.5 h-2.5" /> Scene
                        </span>
                      )}
                      {isMeal && (
                        <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
                          <UtensilsCrossed className="w-2.5 h-2.5" /> Repas
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex-1 space-y-2">
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest">Horaires d'ouverture :</p>
                      {slotFormPointId !== point.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openAddSlotForm(point.id); }}
                          className="text-[10px] bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded transition-colors font-bold flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Ajouter
                        </button>
                      )}
                    </div>

                    {slotFormPointId === point.id && (
                      <div className="bg-[#0A0B0E]/80 border border-indigo-500/30 rounded-xl p-3 space-y-3">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <span className="text-[9px] text-slate-500 uppercase font-bold">Date</span>
                            <input
                              type="date"
                              value={newSlotDate}
                              onChange={e => setNewSlotDate(e.target.value)}
                              className="w-full bg-[#14161B] border border-[#2D3139] rounded px-2 py-1.5 mt-0.5 text-xs text-white focus:outline-none focus:border-indigo-500 [color-scheme:dark]"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <span className="text-[9px] text-slate-500 uppercase font-bold">Début</span>
                            <input
                              type="time"
                              value={newSlotStart}
                              onChange={e => setNewSlotStart(e.target.value)}
                              className="w-full bg-[#14161B] border border-[#2D3139] rounded px-2 py-1.5 mt-0.5 text-xs text-white focus:outline-none focus:border-indigo-500 [color-scheme:dark]"
                            />
                          </div>
                          <div className="flex-1">
                            <span className="text-[9px] text-slate-500 uppercase font-bold">Fin</span>
                            <input
                              type="time"
                              value={newSlotEnd}
                              onChange={e => setNewSlotEnd(e.target.value)}
                              className="w-full bg-[#14161B] border border-[#2D3139] rounded px-2 py-1.5 mt-0.5 text-xs text-white focus:outline-none focus:border-indigo-500 [color-scheme:dark]"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <span className="text-[9px] text-slate-500 uppercase font-bold">Team requis</span>
                            <input
                              type="number" min="1" max="50"
                              value={newSlotTeam}
                              onChange={e => setNewSlotTeam(Number(e.target.value))}
                              className="w-16 bg-[#14161B] border border-[#2D3139] rounded px-2 py-1.5 mt-0.5 text-xs text-white focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                          <label className="flex items-center gap-1.5 cursor-pointer pt-3">
                            <input
                              type="checkbox"
                              checked={newSlotIsHard}
                              onChange={e => setNewSlotIsHard(e.target.checked)}
                              className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-800 text-amber-500 focus:ring-amber-500/20"
                            />
                            <Flame className="w-3 h-3 text-slate-500" />
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Pénibilité</span>
                          </label>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSaveSlot(); }}
                            className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-[10px] font-bold flex items-center justify-center gap-1 transition-colors"
                          >
                            <Check className="w-3 h-3" /> Ajouter
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSlotFormPointId(null); }}
                            className="py-1.5 px-3 bg-[#1A1D23] border border-[#2D3139] text-slate-400 hover:text-white rounded text-[10px] font-bold transition-colors"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                    {slots.length === 0 ? (
                      <p className="text-xs text-slate-500 italic">Aucun horaire configuré</p>
                    ) : (
                      <div className="space-y-2">
                        {visibleSlots.map((slot, idx) => {
                          const slotStart = new Date(parseDateUTCStr(slot.start));
                          const slotEnd = new Date(parseDateUTCStr(slot.end));
                          const assignedTeam = shifts.filter(sh => {
                            if (sh.pointId !== point.id) return false;
                            const shStart = new Date(parseDateUTCStr(sh.start));
                            const shEnd = new Date(parseDateUTCStr(sh.end));
                            return shStart < slotEnd && shEnd > slotStart;
                          }).length;
                          const slotBorder = assignedTeam > 0
                            ? 'border-emerald-500/50'
                            : 'border-red-500/40';

                          return (
                          <div key={idx} onClick={(e) => { e.stopPropagation(); setEditingSlot({ pointId: point.id, idx, start: slot.start, end: slot.end, requiredTeam: slot.requiredTeam || point.requiredTeam || 1, originalStart: slot.start, originalEnd: slot.end }); }}
                            className={`bg-[#0A0B0E]/50 border ${slotBorder} p-3 rounded-xl space-y-1 cursor-pointer hover:border-indigo-500/50 transition-colors group/slotlist`}>
                            <div className="flex justify-between items-start">
                              <p className="text-xs text-slate-200 font-medium leading-relaxed">
                                {formatSlotRange(slot.start, slot.end)}
                              </p>
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${assignedTeam > 0 ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'}`}>
                                  {assignedTeam > 0 ? `${assignedTeam} team` : '0 team'}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const slotShifts = shifts.filter(sh => sh.pointId === point.id && sh.start < slot.end && sh.end > slot.start);
                                  const delMsg = slotShifts.length > 0
                                    ? `Ce créneau a ${slotShifts.length} team assigné(s). Supprimer le créneau supprimera aussi leurs shifts. Continuer ?`
                                    : 'Supprimer ce créneau ?';
                                  if (!window.confirm(delMsg)) return;
                                  const realIdx = point.openingHours.findIndex(oh => oh.start === slot.start && oh.end === slot.end);
                                  if (realIdx === -1) return;
                                  const filtered = point.openingHours.filter((_, j) => j !== realIdx);
                                  updatePoint(point.id, { openingHours: filtered });
                                  }}
                                  className="opacity-0 group-hover/slotlist:opacity-100 p-0.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400 transition-all"
                                  title="Supprimer ce créneau"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                               <span className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter">Team requis :</span>
                               <span className="text-[9px] font-bold text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">{slot.requiredTeam || point.requiredTeam || 1}</span>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {slots.length > 2 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setExpandedCards(prev => ({ ...prev, [point.id]: !isExpanded })); }}
                        className="text-[10px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors py-1 flex items-center gap-1"
                      >
                        {isExpanded ? 'Voir moins' : `Voir les ${slots.length - 2} autres créneaux...`}
                      </button>
                    )}
                  </div>

                  {/* Only allow planning for stage points if artist manager or admin */}
                  {(!point.isStage || isArtistManager || isAdmin) ? (
                    <button
                      onClick={() => setSelectedPointId(point.id)}
                      className="mt-6 w-full py-3 bg-[#1A1D23] border border-[#2D3139] rounded-xl text-sm font-bold text-slate-300 group-hover:text-white group-hover:bg-indigo-600 group-hover:border-indigo-500 transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      <ClipboardList className="w-4 h-4" /> Gérer le Planning
                    </button>
                  ) : (
                    <div className="mt-6 w-full py-3 bg-amber-500/5 border border-amber-500/15 rounded-xl text-sm font-bold text-amber-400/60 text-center">
                      <Music className="w-4 h-4 inline mr-1.5" />Lineup — accès restreint
                    </div>
                  )}
                </div>
              );
            })}
                  </div>
                </CategoryDrawer>
              );
            })}
          </div>
        )}
      </div>
      {editingSlot && (() => {
        const point = points.find(p => p.id === editingSlot.pointId);
        const save = () => {
          if (!point) return;
          const ri = point.openingHours.findIndex(oh => oh.start === editingSlot.originalStart && oh.end === editingSlot.originalEnd);
          if (ri === -1) { setEditingSlot(null); return; }
          const u = [...point.openingHours];
          u[ri] = { ...u[ri], start: editingSlot.start, end: editingSlot.end, requiredTeam: editingSlot.requiredTeam };
          updatePoint(point.id, { openingHours: u });
          setEditingSlot(null);
        };
        const del = () => {
          if (!point) return;
          const ss = shifts.filter(sh => sh.pointId === point.id && sh.start < editingSlot.originalEnd && sh.end > editingSlot.originalStart);
          const dm = ss.length > 0 ? `Ce créneau a ${ss.length} team assigné(s). Supprimer le créneau supprimera aussi leurs shifts. Continuer ?` : 'Supprimer ce créneau ?';
          if (!window.confirm(dm)) return;
          const ri = point.openingHours.findIndex(oh => oh.start === editingSlot.originalStart && oh.end === editingSlot.originalEnd);
          if (ri === -1) { setEditingSlot(null); return; }
          updatePoint(point.id, { openingHours: point.openingHours.filter((_, i) => i !== ri) });
          setEditingSlot(null);
        };
        return (
          <div className="fixed inset-0 z-[160] flex items-center justify-center p-4" style={{backgroundColor:'rgba(0,0,0,0.7)',backdropFilter:'blur(4px)'}}>
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <h3 className="text-white font-semibold mb-1">Modifier le créneau</h3><p className="text-slate-500 text-xs mb-4">{point?.name}</p>
              <div className="space-y-3">
                <div><label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Début</label><input type="datetime-local" value={editingSlot.start.substring(0,16)} onChange={e => setEditingSlot({...editingSlot,start:e.target.value})} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" /></div>
                <div><label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Fin</label><input type="datetime-local" value={editingSlot.end.substring(0,16)} onChange={e => setEditingSlot({...editingSlot,end:e.target.value})} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" /></div>
                <div><label className="block text-[10px] font-semibold text-slate-400 uppercase mb-1">Team requis</label><input type="number" min={0} value={editingSlot.requiredTeam} onChange={e => setEditingSlot({...editingSlot,requiredTeam:parseInt(e.target.value)||0})} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500" /></div>
              </div>
              <div className="flex items-center gap-3 mt-5">
                <button onClick={del} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors">Supprimer</button>
                <div className="flex-1" /><button onClick={()=>setEditingSlot(null)} className="px-3 py-2 bg-[#1A1D23] border border-[#2D3139] text-slate-300 rounded-lg text-xs font-medium transition-colors">Annuler</button>
                <button onClick={save} className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-bold transition-colors">Enregistrer</button>
              </div>
            </div>
          </div>
        );
      })()}
      <OperatingHoursExportModal
        isOpen={showHoursExport}
        onClose={() => setShowHoursExport(false)}
      />
      </>
    );
  }

  return (
    <div className="flex w-full h-full relative overflow-hidden bg-gradient-to-br from-[#0A0B0E] to-[#14161B]">
      <div className="p-4 md:p-6 flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      <div className="flex flex-col flex-none sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <button 
            onClick={() => setSelectedPointId(null)}
            className="flex items-center text-xs font-bold text-indigo-400 hover:text-indigo-300 mb-3 transition-colors bg-indigo-500/10 px-3 py-1.5 rounded-lg border border-indigo-500/20 w-fit"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Points
          </button>
          <h2 className="text-2xl font-bold text-white tracking-wide">{selectedPoint.name}</h2>
          <p className="text-sm text-slate-400 mt-1">{selectedPoint?.isStage ? "Manage shifts for this operational point. Click a block to assign artists." : "Manage shifts for this operational point. Click a block to assign team."}</p>
        </div>
      </div>

      <div className="flex-1 bg-[#14161B]/50 backdrop-blur-xl border border-[#2D3139] rounded-2xl overflow-y-auto p-4 md:p-6 custom-scrollbar shadow-inner relative">
        {pointDates.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            This point has no active opening hours configured. Add opening hours in the Points Configuration to schedule team.
          </div>
        ) : isGeneralPoint ? (
          /* ── General category: one card per opening slot ─────────────── */
          <div className="space-y-4">
            {selectedPoint.openingHours
              .slice()
              .sort((a, b) => a.start.localeCompare(b.start))
              .map(slot => {
                const slotStart = slot.start.substring(0, 16);
                const slotEnd   = slot.end.substring(0, 16);
                // Use overlap logic so old (manually-timed) and new (opening-hours-based) shifts both show
                const slotShifts = shifts.filter(sh =>
                  sh.pointId === selectedPoint.id &&
                  sh.start < slotEnd &&
                  sh.end > slotStart
                );
                const checkedInCount = slotShifts.filter(sh => !!sh.actualStart).length;
                const startHour = parseInt(slotStart.split('T')[1] ?? '0');

                return (
                  <div key={slotStart} className="bg-[#0A0B0E]/80 border border-[#2D3139] rounded-2xl p-5 shadow-lg">
                    {/* Card header */}
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                      <div>
                        <p className="text-xs text-slate-500 capitalize mb-1">{fmtDate(slotStart)}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-lg">{fmtTime(slotStart)} – {fmtTime(slotEnd)}</span>
                          <span className="bg-sky-500/15 text-sky-400 border border-sky-500/25 text-[10px] font-bold px-2 py-0.5 rounded-full">Slot Général</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded-lg ${
                          slotShifts.length > 0 && checkedInCount >= slotShifts.length
                            ? 'bg-emerald-500/15 text-emerald-400'
                            : checkedInCount > 0
                            ? 'bg-amber-500/15 text-amber-400'
                            : 'bg-slate-700/50 text-slate-400'
                        }`}>
                          {checkedInCount}/{slotShifts.length} présents
                        </span>
                        <button
                          onClick={() => setCheckinModal({ slotStart, slotEnd })}
                          className="flex items-center gap-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <ClipboardList className="w-3.5 h-3.5" /> Pointage
                        </button>
                        <button
                          onClick={() => {
                            const pt = selectedPoint;
                            const idx = pt.openingHours.findIndex(s => s.start === slot.start && s.end === slot.end);
                            setEditingSlot({ pointId: pt.id, idx: idx >= 0 ? idx : 0, start: slot.start, end: slot.end, requiredTeam: slot.requiredTeam || pt.requiredTeam, originalStart: slot.start, originalEnd: slot.end });
                          }}
                          className="flex items-center gap-1.5 text-xs font-bold text-slate-300 bg-slate-700/50 hover:bg-slate-600 px-2 py-1.5 rounded-lg transition-colors"
                          title="Modifier le créneau"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Team list */}
                    {slotShifts.length === 0 ? (
                      <div
                        onClick={() => {
                          const startTime = slotStart.split('T')[1]?.substring(0, 5);
                          const endTime   = slotEnd.split('T')[1]?.substring(0, 5);
                          handleOpenAssign(selectedPoint.id, startHour, slotStart.split('T')[0], startTime, endTime);
                        }}
                        className="border-2 border-dashed border-[#2D3139] rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500/40 hover:bg-indigo-500/5 transition-all group"
                      >
                        <Plus className="w-6 h-6 text-slate-600 group-hover:text-indigo-400 mb-2 transition-colors" />
                        <p className="text-slate-500 text-sm group-hover:text-slate-300 transition-colors">{selectedPoint.isStage ? "Cliquez pour assigner un artiste" : "Cliquez pour assigner du team"}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-600 px-1 mb-1">
                          <span>{slotShifts.length} {selectedPoint.isStage ? `artiste${slotShifts.length > 1 ? 's' : ''} assigné${slotShifts.length > 1 ? 's' : ''}` : `team assigné${slotShifts.length > 1 ? 's' : ''}`}</span>
                          <button
                            onClick={() => handleOpenAssign(selectedPoint.id, startHour, slotStart.split('T')[0])}
                            className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors"
                          >
                            <Plus className="w-3 h-3" /> Ajouter
                          </button>
                        </div>
                        {slotShifts.map(sh => {
                          const st = team.find(s => s.id === sh.teamId);
                          const isPresent = !!sh.actualStart;
                          return (
                            <div key={sh.id} className={`flex items-center justify-between rounded-xl px-3 py-2.5 border transition-all ${
                              isPresent
                                ? 'bg-emerald-500/8 border-emerald-500/20'
                                : 'bg-[#14161B] border-[#2D3139]'
                            }`}>
                              <div className="flex items-center gap-2.5">
                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: st?.color || '#6366f1' }} />
                                <span className="text-sm font-medium text-white">{st?.name || 'Inconnu'}</span>
                                {isPresent && (
                                  <span className="text-[10px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" /> Présent
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Retirer ${st?.name || (selectedPoint.isStage ? 'cet artiste' : 'ce team')} de ce slot ?`)) deleteShift(sh.id);
                                }}
                                className="p-1 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                title="Retirer du slot"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="space-y-6">
          {(() => {
            // First, gather all open slots across all dates
            const allActiveSlots: { dateStr: string, hour: number, timestamp: number }[] = [];
            pointDates.forEach(dateStr => {
              for (let h = 0; h < 24; h++) {
                if (isSlotOpen(selectedPoint.id, h, dateStr)) {
                  const ts = new Date(`${dateStr}T${String(h).padStart(2, '0')}:00:00Z`).getTime();
                  allActiveSlots.push({ dateStr, hour: h, timestamp: ts });
                }
              }
            });

            // Sort them chronologically
            allActiveSlots.sort((a, b) => a.timestamp - b.timestamp);

            // Group into contiguous blocks
            const contiguousBlocks: { id: string, slots: typeof allActiveSlots }[] = [];
            if (allActiveSlots.length > 0) {
              let currentBlock = [allActiveSlots[0]];
              for (let i = 1; i < allActiveSlots.length; i++) {
                const prev = allActiveSlots[i - 1];
                const curr = allActiveSlots[i];
                if (curr.timestamp - prev.timestamp <= 3600000) {
                  currentBlock.push(curr);
                } else {
                  contiguousBlocks.push({ id: `block-${currentBlock[0].timestamp}`, slots: currentBlock });
                  currentBlock = [curr];
                }
              }
              contiguousBlocks.push({ id: `block-${currentBlock[0].timestamp}`, slots: currentBlock });
            }

            return contiguousBlocks.map(block => {
              const formatDateObj = (ts: number) => {
                return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(ts));
              };

              const firstSlot = block.slots[0];
              const lastSlot = block.slots[block.slots.length - 1];
              const endTimestamp = lastSlot.timestamp + 3600000;
              
              const dateFirst = formatDateObj(firstSlot.timestamp);
              const dateEnd = formatDateObj(endTimestamp - 1); // Subtract 1ms to get the day where the block effectively ends

              let title = '';
              if (dateFirst === dateEnd) {
                // Formatting End hour
                const endHour = new Date(endTimestamp).getUTCHours();
                const endHourFixed = endHour === 0 ? '00' : String(endHour).padStart(2, '0');
                title = `${dateFirst} de ${String(firstSlot.hour).padStart(2,'0')}h à ${endHourFixed}h`;
              } else {
                const endHour = new Date(endTimestamp).getUTCHours();
                const endDateStr = formatDateObj(endTimestamp); // To say "Samedi 13..."
                title = `Du ${dateFirst} à ${String(firstSlot.hour).padStart(2,'0')}h au ${endDateStr} à ${String(endHour).padStart(2,'0')}h`;
              }

              const blockShiftsList = shifts.filter(s => {
                if (s.pointId !== selectedPoint.id) return false;
                const shiftStart = new Date(parseDateUTCStr(s.start)).getTime();
                const shiftEnd = new Date(parseDateUTCStr(s.end)).getTime();
                
                return block.slots.some(slot => {
                  const cellStart = slot.timestamp;
                  const cellEnd = slot.timestamp + 3600000 - 1;
                  return shiftStart <= cellEnd && shiftEnd >= cellStart + 1;
                });
              });

              const groupedShifts = blockShiftsList.map(s => {
                const shiftStart = new Date(parseDateUTCStr(s.start)).getTime();
                const shiftEnd = new Date(parseDateUTCStr(s.end)).getTime();
                
                const indices = block.slots.map((slot, i) => {
                  const cellStart = slot.timestamp;
                  const cellEnd = slot.timestamp + 3600000 - 1;
                  return shiftStart <= cellEnd && shiftEnd >= cellStart + 1 ? i : -1;
                }).filter(i => i !== -1);
                
                if (indices.length === 0) return null;
                
                const startIdx = Math.min(...indices);
                const endIdx = Math.max(...indices);
                const durationHours = (shiftEnd - shiftStart) / 3600000;
                
                return { ...s, startIdx, endIdx, durationHours: Math.round(durationHours * 10) / 10 };
              }).filter(Boolean);

              const tracks: any[][] = [];
              groupedShifts.forEach(gs => {
                if (!gs) return;
                let placed = false;
                for (let track of tracks) {
                  const overlaps = track.some(sibling => gs.startIdx <= sibling.endIdx && gs.endIdx >= sibling.startIdx);
                  if (!overlaps) {
                    track.push(gs);
                    placed = true;
                    break;
                  }
                }
                if (!placed) tracks.push([gs]);
              });

              return (
                <div key={block.id} className="bg-[#0A0B0E]/80 backdrop-blur-sm border border-[#2D3139] rounded-2xl p-5 shadow-lg">
                  <h4 className="text-white font-bold mb-5 pb-3 border-b border-[#2D3139]/50 text-sm capitalize flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                    {title}
                  </h4>
                  
                  <div className="flex flex-wrap gap-2 pb-2">
                    {block.slots.map((slot, index) => {
                      const slotShifts = getSlotShifts(selectedPoint.id, slot.hour, slot.dateStr);
                      const teamCount = slotShifts.length;
                        const slotRequiredTeam = getSlotRequiredTeam(selectedPoint.id, slot.dateStr, slot.hour);
                        return (
                        <div key={`${slot.dateStr}-${slot.hour}`} className="flex flex-col relative w-[85px] flex-none">
                          {/* Tracks section for this slot */}
                          {tracks.length > 0 && (
                            <div className="flex flex-col space-y-1 mb-2">
                              {tracks.map((track, trackIdx) => {
                                const gs = track.find(t => t && t.startIdx <= index && t.endIdx >= index);
                                if (!gs) return <div key={trackIdx} className="h-5" />;
                                
                                const isStart = gs.startIdx === index;
                                const isEnd = gs.endIdx === index;
                                const isSingle = isStart && isEnd;
                                const isMiddle = Math.floor((gs.startIdx + gs.endIdx) / 2) === index;
                                const st = team.find(teamObj => teamObj.id === gs.teamId);
                                const teamName = st?.name || 'Unknown';
                                const teamColor = st?.color || '#6366f1';
                                
                                return (
                                  <div key={trackIdx} className="h-5 relative flex items-center justify-center">
                                    {!isSingle && (
                                      <div 
                                        className="absolute border-t z-0" 
                                        style={{
                                          borderColor: teamColor,
                                          borderTopWidth: '2px',
                                          opacity: 0.7,
                                          left: isStart ? '50%' : '-8px',
                                          right: isEnd ? '50%' : '-8px'
                                        }}
                                      />
                                    )}
                                    {isMiddle && (
                                      <span 
                                        className="px-1.5 z-10 whitespace-nowrap text-[10px] rounded font-medium"
                                        style={{
                                          backgroundColor: '#0A0B0E',
                                          color: teamColor,
                                          border: `1px solid ${teamColor}`
                                        }}
                                      >
                                        {teamName.split(' ')[0]} : {gs.durationHours}h
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div 
                            onClick={() => handleOpenAssign(selectedPoint.id, slot.hour, slot.dateStr)}
                            className={`w-full min-h-[60px] flex-none rounded border flex flex-col p-1.5 cursor-pointer hover:brightness-125 transition-all ${getSlotColor(teamCount, slotRequiredTeam)}`}
                          >
                            <div className="text-[10px] font-bold text-center border-b border-white/10 pb-1 mb-1">{String(slot.hour).padStart(2, '0')}h-{String((slot.hour + 1) % 24).padStart(2, '0')}h</div>
                            <span className="text-[10px] font-medium text-center mb-1 text-white/80">{teamCount}/{slotRequiredTeam} {selectedPoint.isStage ? 'artiste' : 'staff'}</span>
                            
                            {slotShifts.length > 0 && (
                              <div className="flex flex-col w-full space-y-1 mt-1">
                                {slotShifts.map(s => {
                                  const st = team.find(teamObj => teamObj.id === s.teamId);
                                  return (
                                    <div key={s.id} 
                                      className="w-full rounded flex items-center justify-center text-[9px] text-white py-[3px] px-1 truncate pointer-events-auto" 
                                      title={st?.name}
                                      style={{ backgroundColor: st?.color || '#6366f1' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenAssign(selectedPoint.id, slot.hour, slot.dateStr);
                                      }}>
                                      {st?.name.split(' ')[0]}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              );
            });
          })()}
          </div>
        )}
      </div>
      </div>

      {/* Global Team Schedule Sidebar */}
      <div className={`absolute lg:relative top-0 right-0 h-full z-30 bg-[#0A0B0E]/95 backdrop-blur-2xl border-l border-[#2D3139] transition-all duration-300 flex flex-col shadow-2xl ${isTeamPanelOpen ? 'w-full md:w-[400px] translate-x-0' : 'w-0 translate-x-full border-l-0 lg:border-l-0 lg:w-0 overflow-hidden'}`}>
        <div className="p-5 border-b border-[#2D3139]/50 bg-[#1A1D23]/50 flex justify-between items-center whitespace-nowrap">
           <h3 className="text-white font-bold tracking-wide">Global Schedule</h3>
           <button onClick={() => setIsTeamPanelOpen(false)} className="text-slate-400 hover:text-white bg-[#2D3139]/50 p-1.5 rounded-lg transition-colors"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar">
           {visibleTeamForAssignment.map(s => {
               const teamShifts = shifts.filter(sh => sh.teamId === s.id);
               if (teamShifts.length === 0) return null;
               
               // Sort shifts by start time
               teamShifts.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

               let totalMs = 0;
               teamShifts.forEach(sh => {
                 const p = (allPoints || points).find(p => p.id === sh.pointId);
                 if (p) {
                   const cat = categories.find(c => c.id === p.categoryId);
                   if (!cat?.isGeneral) {
                     totalMs += new Date(sh.end).getTime() - new Date(sh.start).getTime();
                   }
                 }
               });
               const totalHours = (totalMs / (1000 * 60 * 60)).toFixed(1);

               return (
                  <div key={s.id} className="bg-[#14161B] rounded-lg p-3 border border-[#2D3139]">
                     <div className="flex justify-between items-start">
                       <h4 className="font-semibold text-white text-sm">{s.name}</h4>
                       <span className="text-xs text-indigo-400 font-medium bg-indigo-500/10 px-2 py-0.5 rounded">{totalHours}h</span>
                     </div>
                     <div className="mt-2 space-y-2">
                        {teamShifts.map(sh => {
                            const point = (allPoints || points).find(p => p.id === sh.pointId);
                            const startDate = new Date(sh.start);
                            const endDate = new Date(sh.end);
                            const isCurrentPoint = point?.id === selectedPointId;
                            return (
                               <div key={sh.id} className="text-xs flex items-start gap-2 bg-[#1A1D23] p-2 rounded">
                                   <div className={`w-2 h-2 mt-1 rounded-full ${isCurrentPoint ? 'bg-indigo-500' : 'bg-slate-500'}`} />
                                   <div>
                                       <div className={isCurrentPoint ? "text-indigo-300" : "text-slate-200"}>{point?.name || 'Unknown Point'}</div>
                                       <div className="text-slate-400">
                                          {(() => {
                                         const shiftCat = categories.find(c => c.id === point?.categoryId);
                                         if (shiftCat?.isGeneral) return (
                                           <span className="inline-flex items-center gap-1 bg-sky-500/15 text-sky-400 border border-sky-500/25 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                             ✓ Slot • {startDate.toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit', timeZone: 'UTC'})} – {endDate.toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit', timeZone: 'UTC'})}
                                           </span>
                                         );
                                         return <>{startDate.toLocaleDateString('fr-FR', {day: '2-digit', month:'2-digit', timeZone: 'UTC'})} {startDate.toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit', timeZone: 'UTC'})} – {endDate.toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit', timeZone: 'UTC'})}</>;
                                       })()
                                       }
                                      </div>
                                   </div>
                               </div>
                            )
                        })}
                     </div>
                  </div>
               );
           })}
           {visibleTeamForAssignment.every(s => !shifts.some(sh => sh.teamId === s.id)) && (
               <div className="text-slate-500 text-sm text-center">No shifts assigned yet.</div>
           )}
        </div>
      </div>

      {!isTeamPanelOpen && (
        <button 
           onClick={() => setIsTeamPanelOpen(true)}
           className="absolute top-1/2 right-0 -translate-y-1/2 z-20 bg-[#1A1D23] border border-r-0 border-[#2D3139] text-indigo-400 rounded-l-md p-2 shadow-[0_0_15px_rgba(0,0,0,0.5)] hover:text-indigo-300 hover:bg-[#2A2E35] transition-transform"
           title={selectedPoint?.isStage ? "View global artist schedule" : "View global team schedule"}
        >
           <Users size={20} />
        </button>
      )}

      {/* Assignment Modal */}
      {isModalOpen && modalData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-xl w-full max-w-md shadow-2xl flex flex-col">
            <div className="p-6 border-b border-[#2D3139]">
              <h3 className="text-lg font-semibold">{selectedPoint?.isStage ? "Assign Artist" : "Assign Team"}</h3>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <p className="text-slate-400 text-sm">{selectedPoint.name} • {modalData.dateStr} {String(modalData.hour).padStart(2,'0')}:00</p>
                {isGeneralPoint && <span className="bg-sky-500/20 text-sky-400 border border-sky-500/30 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Slot Général</span>}
              </div>
            </div>
            
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
              {validationErrors.length > 0 && (
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                  {validationErrors.map((err, i) => (
                    <div key={i} className={`p-3 rounded border text-sm ${err.type === 'error' ? 'bg-red-500/10 border-red-500/50 text-red-400' : 'bg-yellow-500/10 border-yellow-500/50 text-yellow-500'}`}>
                      {err.message}
                    </div>
                  ))}
                  {validationErrors.some(e => e.type === 'error') ? (
                    <div className="text-xs text-slate-500">Please resolve errors to assign.</div>
                  ) : (
                    <div className="text-xs text-yellow-500">Warnings present. Proceed with caution.</div>
                  )}
                </div>
              )}

              {isGeneralPoint ? (
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-slate-300">{selectedPoint?.isStage ? "Select Artists (Bulk Assignment)" : "Select Team (Bulk Assignment)"}</label>
                    <button 
                      onClick={() => {
                        const filteredIds = visibleTeamForAssignment
                          .filter(s => s.name.toLowerCase().includes(teamSearchQuery.toLowerCase()))
                          .map(s => s.id);
                        
                        // If all filtered are already selected, deselect them
                        const allSelected = filteredIds.every(id => assignTeamIds.includes(id));
                        if (allSelected) {
                           setAssignTeamIds(assignTeamIds.filter(id => !filteredIds.includes(id)));
                        } else {
                           // Select any that aren't already selected
                           const newIds = [...assignTeamIds];
                           filteredIds.forEach(id => {
                              if (!newIds.includes(id)) newIds.push(id);
                           });
                           setAssignTeamIds(newIds);
                        }
                        setValidationErrors([]);
                      }}
                      className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
                    >
                      {visibleTeamForAssignment.filter(s => s.name.toLowerCase().includes(teamSearchQuery.toLowerCase())).every(s => assignTeamIds.includes(s.id)) ? 'Désélectionner tout' : 'Toute l\'équipe'}
                    </button>
                  </div>
                  <input 
                    type="text"
                    placeholder={selectedPoint?.isStage ? "Search artists..." : "Search team..."}
                    value={teamSearchQuery}
                    onChange={e => setTeamSearchQuery(e.target.value)}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded px-3 py-2 text-white text-sm mb-2 focus:outline-none focus:border-indigo-500"
                  />
                  <div className="max-h-40 overflow-y-auto border border-[#2D3139] rounded bg-[#0A0B0E] space-y-1 p-2 custom-scrollbar">
                    {visibleTeamForAssignment.filter(s => s.name.toLowerCase().includes(teamSearchQuery.toLowerCase())).map(s => (
                      <label key={s.id} className="flex items-center space-x-3 p-2 hover:bg-[#1A1D23] rounded cursor-pointer transition-colors">
                        <input 
                          type="checkbox"
                          checked={assignTeamIds.includes(s.id)}
                          onChange={(e) => {
                            if (e.target.checked) setAssignTeamIds([...assignTeamIds, s.id]);
                            else setAssignTeamIds(assignTeamIds.filter(id => id !== s.id));
                            setValidationErrors([]);
                          }}
                          className="w-4 h-4 rounded border-[#2D3139] bg-[#14161B] text-indigo-500 focus:ring-offset-0 focus:ring-0"
                        />
                        <span className="text-sm text-slate-200">
                          {s.name} <span className="text-slate-500 text-xs ml-1">({(s.roles || [s.role]).join(', ')})</span>
                          {s.unavailableDates?.includes(modalData.dateStr) && <span className="text-red-400 font-medium ml-2 text-xs">[Indisponible]</span>}
                        </span>
                      </label>
                    ))}
                    {visibleTeamForAssignment.filter(s => s.name.toLowerCase().includes(teamSearchQuery.toLowerCase())).length === 0 && (
                      <div className="text-slate-500 text-sm text-center py-2">{selectedPoint?.isStage ? "No artists found" : "No team found"}</div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">{selectedPoint?.isStage ? "Select Artist" : "Select Team"}</label>
                  <select 
                    className="w-full bg-[#1A1D23] border border-[#2D3139] rounded px-3 py-2 text-white outline-none focus:border-indigo-500"
                    value={assignTeamId}
                    onChange={(e) => {
                      setAssignTeamId(e.target.value);
                      setValidationErrors([]); 
                    }}
                  >
                    <option value="">{selectedPoint?.isStage ? "-- Choose Artist --" : "-- Choose Team --"}</option>
                    {visibleTeamForAssignment.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({(s.roles || [s.role]).join(', ')})
                        {s.unavailableDates?.includes(modalData.dateStr) ? ' [Indisponible]' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* General slot auto-timing info */}
              {isGeneralPoint && (
                <div className="bg-sky-500/5 border border-sky-500/20 rounded-xl p-3 flex items-start gap-2">
                  <span className="text-sky-400 text-sm flex-shrink-0">⏰</span>
                  <p className="text-sky-300/80 text-xs leading-relaxed">
                    {selectedPoint?.isStage 
                      ? <>Cet <strong>artiste</strong> sera assigné sur <strong>toute la durée d'ouverture</strong> du point. Les horaires sont automatiquement repris depuis les créneaux configurés.</>
                      : <>Ce <strong>team</strong> sera assigné sur <strong>toute la durée d'ouverture</strong> du point. Les horaires sont automatiquement repris depuis les créneaux configurés.</>
                    }
                  </p>
                </div>
              )}

              {!isGeneralPoint && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Start Time</label>
                  <input 
                    type="time"
                    className="w-full bg-[#1A1D23] border border-[#2D3139] rounded px-3 py-2 text-white"
                    value={assignStartTime}
                    onChange={(e) => {
                      setAssignStartTime(e.target.value);
                      setValidationErrors([]);
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">End Time</label>
                  <input 
                    type="time"
                    value={assignEndTime}
                    onChange={e => {
                      setAssignEndTime(e.target.value);
                      setValidationErrors([]);
                    }}
                    className="w-full bg-[#1A1D23] border border-[#2D3139] rounded px-3 py-2 text-white"
                  />
                </div>
              </div>
              )}

              {/* Show currently assigned shifts in this slot to allow removing */}
              {(() => {
                // For general points, match by actual assignStartTime/assignEndTime window; for others, use hour cell
                const slotStart2 = `${modalData!.dateStr}T${assignStartTime}`;
                const slotEnd2   = `${modalData!.dateStr}T${assignEndTime}`;
                const existingShifts = isGeneralPoint
                  ? shifts.filter(s => s.pointId === modalData!.pointId && s.start < slotEnd2 && s.end > slotStart2)
                  : getSlotShifts(modalData!.pointId, modalData!.hour, modalData!.dateStr);
                if (existingShifts.length === 0) return null;
                return (
                <div className="mt-4 border-t border-[#2D3139] pt-4">
                  <p className="text-sm font-medium text-slate-300 mb-2">Already Assigned Here:</p>
                  <div className="space-y-2">
                      {existingShifts.map(s => {
                      const assignedTeam = team.find(st => st.id === s.teamId);
                      const canRemove = currentUser.role === 'admin' ||
                                        assignedTeam?.categoryId === currentUser.categoryId ||
                                        (allPoints || points).find(p => p.id === modalData!.pointId)?.categoryId === currentUser.categoryId;
                      return (
                      <div key={s.id} className="flex justify-between items-center bg-[#1A1D23] px-3 py-2 rounded text-sm gap-2">
                        <div className="flex flex-col min-w-0">
                          <span className="font-medium text-white truncate">{assignedTeam?.name || 'Unknown Team'}</span>
                          <span className="text-[10px] text-slate-500">
                            {s.start.substring(11, 16)} – {s.end.substring(11, 16)}
                          </span>
                        </div>
                        {canRemove && (
                          <button onClick={() => {
                            if (window.confirm(`Supprimer le shift de ${assignedTeam?.name || 'ce team'} (${s.start.substring(11,16)}–${s.end.substring(11,16)}) ?`)) {
                              deleteShift(s.id);
                            }
                          }} className="flex-shrink-0 text-red-400 hover:text-red-300 text-xs font-bold bg-red-500/10 hover:bg-red-500/20 px-2 py-1 rounded transition-colors">✕ Retirer</button>
                        )}
                      </div>
                      )
                    })}
                  </div>
                </div>
                );
              })()}
            </div>

            <div className="p-6 border-t border-[#2D3139] flex justify-end gap-3 bg-[#0A0B0E] rounded-b-xl">
               <button 
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white"
               >
                 Cancel
               </button>
               <button 
                onClick={handleAssign}
                disabled={(isGeneralPoint ? assignTeamIds.length === 0 : !assignTeamId) || validationErrors.some(e => e.type === 'error')}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-sm font-medium disabled:opacity-50"
               >
                 {validationErrors.length > 0 && !validationErrors.some(e => e.type === 'error') ? "Ignorer et Confirmer" : isGeneralPoint ? "Confirmer le Slot" : "Assigner le Shift"}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Tailwind helper class for hatched pattern */}
      <style>{`
        .diagonal-stripes {
          background-image: repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.02) 10px, rgba(255,255,255,0.02) 20px);
        }
      `}</style>

      {/* ── Check-in Modal (General slots) ─────────────────────────── */}
      {checkinModal && selectedPoint && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-md shadow-2xl flex flex-col max-h-[80vh]">
            {/* Header */}
            <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-indigo-400" /> Pointage Manuel
                </h3>
                <p className="text-slate-400 text-sm mt-0.5">
                  {selectedPoint.name} · {fmtTime(checkinModal.slotStart)} – {fmtTime(checkinModal.slotEnd)}
                </p>
              </div>
              <button onClick={() => setCheckinModal(null)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-[#2D3139] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stats bar */}
            <div className="px-5 py-3 bg-[#0A0B0E]/60 flex gap-6 border-b border-[#2D3139]">
              <div className="text-center">
                <div className="text-xl font-bold text-emerald-400">{checkinModalShifts.filter(s => !!s.actualStart).length}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">Présents</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-amber-400">{checkinModalShifts.filter(s => !s.actualStart).length}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">En attente</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-white">{checkinModalShifts.length}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">Total</div>
              </div>
            </div>

            {/* Team list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
              {checkinModalShifts.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">Aucun team assigné à ce slot</p>
              ) : checkinModalShifts.map(sh => {
                const st = team.find(s => s.id === sh.teamId);
                const isPresent = !!sh.actualStart;
                const checkinTime = sh.actualStart
                  ? new Date(sh.actualStart).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : null;
                return (
                  <div key={sh.id} className={`flex items-center justify-between rounded-xl px-4 py-3 border transition-all ${
                    isPresent ? 'bg-emerald-500/8 border-emerald-500/20' : 'bg-[#0A0B0E] border-[#2D3139]'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: st?.color || '#6366f1' }}>
                        {st?.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{st?.name || 'Inconnu'}</p>
                        {checkinTime && <p className="text-[11px] text-emerald-400">Pointé à {checkinTime}</p>}
                      </div>
                    </div>
                    {isPresent ? (
                      <button
                        onClick={() => updateShift(sh.id, { actualStart: undefined })}
                        className="text-[11px] text-slate-400 hover:text-red-400 bg-[#1A1D23] hover:bg-red-500/10 border border-[#2D3139] hover:border-red-500/30 px-3 py-1.5 rounded-lg transition-all font-medium"
                      >Annuler</button>
                    ) : (
                      <button
                        onClick={() => updateShift(sh.id, { actualStart: new Date().toISOString() })}
                        className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Présent
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-[#2D3139]">
              <button onClick={() => setCheckinModal(null)}
                className="w-full py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-white font-medium rounded-xl text-sm transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <OperatingHoursExportModal
        isOpen={showHoursExport}
        onClose={() => setShowHoursExport(false)}
      />

    </div>
  );
}
